import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
  Box,
  Paper,
  Chip,
  Tooltip,
  Avatar,
  Stack,
} from "@mui/material";
import SummarizeIcon from "@mui/icons-material/Summarize";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalAtmOutlinedIcon from "@mui/icons-material/LocalAtmOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import ListIcon from "@mui/icons-material/List";
import DetailItem from "../../../../Components/DetailItem";

const getStatusColor = (status) => {
  const statusColors = {
    Delivered: "success",
    "Out for Delivery": "info",
    Processing: "warning",
    Pending: "default",
    Canceled: "error",
    Shipped: "primary",
  };
  return statusColors[status] || "default";
};

const OrderDetailsSection = ({ orderData, formatDate }) => {
  const items = orderData?.items || [];
  const subtotal =
    items.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <Grid item xs={12}>
      <Accordion elevation={2} defaultExpanded={false}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: "primary.50",
            "&:hover": { backgroundColor: "primary.100" },
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SummarizeIcon color="primary" />
            <Typography variant="h6">Order Details</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f9f9f9" }}>
          <Grid container spacing={3}>
            {/* Order Information */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  border: "1px solid #e0e0e0",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderColor: "primary.light",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="600" color="primary">
                      Order Information
                    </Typography>
                  </Box>
                  {orderData?.currentStatus && (
                    <Chip
                      label={orderData.currentStatus}
                      color={getStatusColor(orderData.currentStatus)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Divider sx={{ mb: 2.5 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <DetailItem
                      label="Order No."
                      value={orderData?.orderId}
                      copyable={true}
                      icon={<LocalAtmOutlinedIcon fontSize="small" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailItem
                      label="Order Date"
                      value={
                        orderData?.orderDate
                          ? formatDate(orderData.orderDate)
                          : null
                      }
                      icon={<EventOutlinedIcon fontSize="small" />}
                    />
                  </Grid>
                  {orderData?.bay && (
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Bay" value={orderData.bay} />
                    </Grid>
                  )}
                  {orderData?.estimatedDelivery &&
                    orderData?.currentStatus !== "Delivered" &&
                    orderData?.currentStatus !== "Canceled" && (
                      <Grid item xs={12} sm={6}>
                        <Tooltip
                          title="Expected delivery date"
                          arrow
                          placement="top"
                        >
                          <Box>
                            <DetailItem
                              label="Estimated Delivery Date"
                              value={formatDate(orderData.estimatedDelivery)}
                              valueColor="primary.main"
                              valueWeight="bold"
                              icon={
                                <LocalShippingOutlinedIcon fontSize="small" />
                              }
                            />
                          </Box>
                        </Tooltip>
                      </Grid>
                    )}
                  {orderData?.deliveredDate && (
                    <Grid item xs={12} sm={6}>
                      <DetailItem
                        label="Delivered On"
                        value={formatDate(orderData.deliveredDate)}
                        valueColor="success.main"
                        valueWeight="bold"
                        icon={<CheckCircleOutlineIcon fontSize="small" />}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: "8px",
                        border: "1px solid",
                        borderColor: "grey.300",
                      }}
                    >
                      <DetailItem
                        label="Total Amount"
                        value={
                          orderData?.totalAmount
                            ? `$${orderData.totalAmount.toFixed(2)}`
                            : "N/A"
                        }
                        valueColor="primary.main"
                        valueWeight="bold"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Order Items */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  border: "1px solid #e0e0e0",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderColor: "primary.light",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ListIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="600" color="primary">
                      Items
                    </Typography>
                  </Box>
                  {items.length > 0 && (
                    <Chip
                      label={`${items.length} ${
                        items.length === 1 ? "item" : "items"
                      }`}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Divider sx={{ mb: 2.5 }} />
                {items.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 6,
                      color: "text.secondary",
                    }}
                  >
                    <InventoryOutlinedIcon
                      sx={{ fontSize: 64, opacity: 0.3, mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No items in this order
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {items.map((item, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 2.5,
                          borderRadius: "12px",
                          border: "1px solid #e0e0e0",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                            borderColor: "primary.light",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Avatar
                          variant="rounded"
                          src={item.image || item.imageUrl}
                          sx={{
                            backgroundColor:
                              item.image || item.imageUrl
                                ? "transparent"
                                : "primary.light",
                            width: 72,
                            height: 72,
                            border: "2px solid",
                            borderColor: "grey.200",
                          }}
                        >
                          {!item.image && !item.imageUrl && (
                            <ShoppingBagOutlinedIcon
                              sx={{ color: "primary.main", fontSize: 32 }}
                            />
                          )}
                        </Avatar>
                        <Box flexGrow={1}>
                          {item.stockCode && (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              sx={{ mb: 0.5 }}
                            >
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                Stock Code: {item.stockCode}
                              </Typography>
                            </Stack>
                          )}
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            sx={{ mb: 1 }}
                          >
                            Description: {item.name || "Unknown Item"}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            <Chip
                              label={`Qty: ${item.quantity}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ fontWeight: 600 }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ${item.price?.toFixed(2) || "0.00"} each
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Subtotal
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="primary.main"
                          >
                            ${((item.price || 0) * item.quantity).toFixed(2)}
                          </Typography>
                        </Box>
                      </Paper>
                    ))}

                    {/* Summary Section - Only show if more than 1 item */}
                    {items.length > 1 && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            backgroundColor: "grey.50",
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: "grey.300",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body1" color="text.secondary">
                              Total Qty:
                            </Typography>
                            <Typography variant="body1" fontWeight="600">
                              {itemCount}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="h6" fontWeight="bold">
                              Order Total:
                            </Typography>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              color="primary.main"
                            >
                              ${subtotal.toFixed(2)}
                            </Typography>
                          </Box>
                        </Paper>
                      </>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

export default OrderDetailsSection;
